import type { BusinessMode, Org, OrgCapability, ServiceProvider, Vendor } from '@shopgnd/shared';
import { defaultModesForBusinessType } from '@shopgnd/shared';

/** Map storefront business modes → workspace capabilities. */
export function capabilitiesFromBusinessModes(modes: BusinessMode[]): OrgCapability[] {
  const caps = new Set<OrgCapability>();
  for (const mode of modes) {
    if (mode === 'shop') caps.add('shop');
    if (mode === 'book' || mode === 'hire') caps.add('bookings');
    if (mode === 'listings') caps.add('property');
  }
  if (caps.has('shop') || caps.has('bookings')) caps.add('classifieds');
  if (caps.has('shop')) caps.add('payouts');
  return [...caps];
}

export function deriveCapabilitiesFromVendor(vendor: Vendor): OrgCapability[] {
  const modes =
    vendor.businessModes?.length ? vendor.businessModes : defaultModesForBusinessType(vendor.businessType);
  return capabilitiesFromBusinessModes(modes);
}

export function orgHasCapability(capabilities: OrgCapability[], cap: OrgCapability): boolean {
  return capabilities.includes(cap);
}

/**
 * Union persisted org capabilities with legacy vendor/pro profiles.
 * Prevents hub redirect loops when Org rows were created without caps.
 */
export function mergeOrgCapabilities(
  org: Pick<Org, 'capabilities'>,
  vendor?: Vendor | null,
  pro?: ServiceProvider | null,
): OrgCapability[] {
  const set = new Set<OrgCapability>(org.capabilities ?? []);
  if (vendor) deriveCapabilitiesFromVendor(vendor).forEach((c) => set.add(c));
  if (pro) {
    set.add('bookings');
    set.add('classifieds');
  }
  if (set.size > 0) return [...set];
  if (vendor) return deriveCapabilitiesFromVendor(vendor);
  if (pro) return ['bookings', 'classifieds'];
  return [];
}
